import { siteServiceOptions as DEFAULT_SERVICE_OPTIONS } from "./themeColors";

const CC_OFFERING_CODES = {
	amfmethod_premium: ["860580000", "860580003", "860580004"],
	service_plan: ["860580001"],
	sustainable_oven_service: ["860580002"],
	machine_dashboard_ixon: [],
	predictive_maintenance: [],
};

const CC_OFFERING_SERVICE_OPTIONS = [
	{ key: "service_plan", label: "Service Plan" },
	{ key: "amfmethod_premium", label: "AMFPremium" },
	{ key: "sustainable_oven_service", label: "Sustainable Oven Service" },
	{ key: "machine_dashboard_ixon", label: "Machine Dashboard (IXON)" },
	{ key: "predictive_maintenance", label: "Predictive Maintenance" },
];

const normalizeServiceOptions = (raw) => {
	if (!raw) return null;
	const parsed = Array.isArray(raw)
		? raw
		: (() => {
			if (typeof raw !== "string") return null;
			try {
				return JSON.parse(raw);
			} catch {
				return null;
			}
		})();

	if (!Array.isArray(parsed)) return null;

	const sanitized = parsed
		.map((item) => ({
			key: String(item?.key || "").trim(),
			label: String(item?.label || "").trim(),
			description: String(item?.description || "").trim(),
			href: String(item?.href || "").trim(),
		}))
		.filter((item) => item.key && item.label);

	return sanitized.length > 0 ? sanitized : null;
};

export const resolveServiceOptions = (optionKeys) => {
	const fromOptions = normalizeServiceOptions(optionKeys?.customerLocationServices);
	const defaultOptions =
		Array.isArray(DEFAULT_SERVICE_OPTIONS) && DEFAULT_SERVICE_OPTIONS.length > 0
			? DEFAULT_SERVICE_OPTIONS
			: CC_OFFERING_SERVICE_OPTIONS;

	if (!fromOptions) {
		return defaultOptions.slice();
	}

	const configuredByKey = new Map(
		fromOptions
			.filter((item) => item?.key)
			.map((item) => [item.key, item])
	);

	const mergedDefaults = defaultOptions.map((item) => ({
		...item,
		...(configuredByKey.get(item.key) || {}),
	}));

	const extraConfigured = fromOptions.filter(
		(item) => item?.key && !defaultOptions.some((defaultItem) => defaultItem.key === item.key)
	);

	return [...mergedDefaults, ...extraConfigured];
};

export const getCcOfferingServiceOptions = () => resolveServiceOptions({});

const parseCcOfferings = (raw) => {
	if (!raw) return new Set();
	if (Array.isArray(raw)) {
		return new Set(raw.map((val) => String(val).trim()).filter(Boolean));
	}
	if (typeof raw === "object") {
		const flags = {};
		Object.keys(CC_OFFERING_CODES).forEach((key) => {
			flags[key] = Boolean(raw?.[key]);
		});
		return flags;
	}
	if (typeof raw === "string") {
		return new Set(
			raw
				.split(",")
				.map((val) => String(val).trim())
				.filter(Boolean)
		);
	}
	return new Set();
};

export const resolveCcOfferingFlags = (raw) => {
	const parsed = parseCcOfferings(raw);
	if (parsed && !(parsed instanceof Set)) {
		return {
			amfmethod_premium: Boolean(parsed?.amfmethod_premium),
			service_plan: Boolean(parsed?.service_plan),
			sustainable_oven_service: Boolean(parsed?.sustainable_oven_service),
			machine_dashboard_ixon: Boolean(parsed?.machine_dashboard_ixon),
			predictive_maintenance: Boolean(parsed?.predictive_maintenance),
		};
	}
	const codes = parsed instanceof Set ? parsed : new Set();
	return {
		amfmethod_premium: CC_OFFERING_CODES.amfmethod_premium.some((code) => codes.has(code)),
		service_plan: CC_OFFERING_CODES.service_plan.some((code) => codes.has(code)),
		sustainable_oven_service: CC_OFFERING_CODES.sustainable_oven_service.some((code) => codes.has(code)),
		machine_dashboard_ixon: CC_OFFERING_CODES.machine_dashboard_ixon.some((code) => codes.has(code)),
		predictive_maintenance: CC_OFFERING_CODES.predictive_maintenance.some((code) => codes.has(code)),
	};
};

export const serializeServiceOptions = (services = []) => {
	const sanitized = (Array.isArray(services) ? services : [])
		.map((item) => ({
			key: String(item?.key || "").trim(),
			label: String(item?.label || "").trim(),
			description: String(item?.description || "").trim(),
			href: String(item?.href || "").trim(),
		}))
		.filter((item) => item.key && item.label);
	return JSON.stringify(sanitized);
};

export const getCustomerLocationOptionFlags = (optionKeys) => ({
	servicesEnabled: optionKeys?.customerLocationServicesEnabled !== "false",
	showLegalEntity: optionKeys?.customerLocationShowLegalEntity !== "false",
	showSite: optionKeys?.customerLocationShowSite !== "false",
	showWarehouse: optionKeys?.customerLocationShowWarehouse !== "false",
	showPaymentTerms: optionKeys?.customerLocationShowPaymentTerms !== "false",
	showOnHold: optionKeys?.customerLocationShowOnHold !== "false",
});
